import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { Button, Card, Chip, EmptyState, ErrorNotice, KV, PageHeader, Skeleton } from '@/components/ui';
import { useI18n, formatDateTime } from '@/i18n';
import { api } from '@/lib/api';
import { ME_KEY, type Me } from '@/lib/auth';
import { money } from '@/lib/format';
import { usePublicSettings } from '@/lib/settings';
import type { BR } from '@/lib/types';
import { Countdown } from '@/components/ui';
import { AssignmentErrorNotice, assignmentFailure } from '@/features/shared/RequestParts';
import { DeclineDialog } from './Dashboard';

export function BrokerIncoming({ me }: { me: Me }) {
  const { t, locale, localTitle } = useI18n();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [declining, setDeclining] = useState<BR | null>(null);
  const [error, setError] = useState<string | null>(null);
  const list = useQuery({ queryKey: ['broker', 'incoming'], queryFn: () => api.get<{ items: BR[] }>('/api/broker/incoming'), refetchInterval: 15_000 });
  const { acceptWindowMinutes: windowMin } = usePublicSettings();
  const act = useMutation({
    mutationFn: (input: { id: string; action: 'accept' | 'decline'; reason?: string }) => api.post(`/api/broker/requests/${input.id}/${input.action}`, input.action === 'decline' ? { reason: input.reason } : {}),
    onSuccess: (_r, input) => { setDeclining(null); setError(null); void qc.invalidateQueries({ queryKey: ['broker'] }); void qc.invalidateQueries({ queryKey: ME_KEY }); if (input.action === 'accept') navigate(`/broker/requests/${input.id}`); },
    // A stale card (declined, reassigned, expired meanwhile): say so and refetch so it disappears.
    onError: (e) => { setDeclining(null); setError(assignmentFailure(e)); void qc.invalidateQueries({ queryKey: ['broker'] }); },
  });
  if (list.isLoading) return <Skeleton lines={6} />;
  if (list.error) return <ErrorNotice error={list.error} retry={() => void list.refetch()} labels={{ message: t('app.error'), retry: t('app.retry') }} />;
  const items = list.data?.items ?? [];
  const max = me.broker?.slots.max ?? 0;
  return (
    <div className="flex flex-col gap-6">
      <PageHeader crumbs={[{ to: '/broker', label: t('nav.dashboard') }, { label: t('nav.incoming') }]} title={t('nav.incoming')} meta={t('broker.incoming.intro', { n: windowMin })} />
      <AssignmentErrorNotice error={error} />
      {items.length ? items.map((r) => (
        <Card key={r.id} lift>
          <div className="flex flex-wrap justify-between gap-4">
            <div className="min-w-0">
              <span className="text-muted text-[13px]"><span className="num">{r.number}</span> · {t('broker.incoming.assignedAt', { time: r.assignedAt ? formatDateTime(r.assignedAt, locale) : '' })}</span>
              <h2 className="mt-1.5 mb-1">{localTitle(r)}</h2>
              {r.property ? (
                <p className="text-muted text-[14px]">
                  <span className="num">{r.property.areaM2} {t('app.m2')}</span>
                  {r.property.bedrooms != null ? <span> · <span className="num">{r.property.bedrooms}</span> {locale === 'ar' ? 'غرف' : 'beds'}</span> : null}
                  {r.property.streetWidthM != null ? <span> · {locale === 'ar' ? 'شارع' : 'street'} <span className="num">{r.property.streetWidthM} م</span></span> : null}
                  {r.property.ownerEstimateHalalas != null ? <span> · {t('dashboard.ownerEstimate')} <span className="num">{money(r.property.ownerEstimateHalalas, locale)}</span></span> : null}
                </p>
              ) : null}
            </div>
            <div className="text-center">
              <p className="label">{t('broker.incoming.remaining')}</p>
              <p className="num text-[36px] font-light text-warning leading-tight">{r.acceptDeadlineAt ? <Countdown deadline={r.acceptDeadlineAt} expiredLabel={t('dashboard.expired')} /> : '—'}</p>
            </div>
          </div>
          <KV className="mt-4" items={[
            { k: t('broker.incoming.owner'), v: `${r.ownerName ?? '—'} · ${t('broker.incoming.phoneAfterAccept')}` },
            { k: t('broker.incoming.location'), v: `${r.property?.district ?? ''}${r.property?.city ? `، ${r.property.city}` : ''} ${t('broker.incoming.districtOnly')}` },
            ...(r.adminNotes ? [{ k: t('broker.incoming.adminNote'), v: <span className="text-fg-2">«{r.adminNotes}»</span> }] : []),
            ...(r.property?.notes ? [{ k: t('broker.incoming.ownerNotes'), v: <span className="text-fg-2">«{r.property.notes}»</span> }] : []),
          ]} />
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <Button variant="accent" className="min-w-[180px]" loading={act.isPending && act.variables?.id === r.id} onClick={() => act.mutate({ id: r.id, action: 'accept' })}>{t('broker.incoming.acceptRequest')}</Button>
            <Button variant="secondary" onClick={() => setDeclining(r)}>{t('broker.incoming.decline')}</Button>
            <span className="text-muted text-[13px] ms-auto">{t('broker.incoming.acceptTakesSlot', { n: max })}</span>
          </div>
        </Card>
      )) : <EmptyState title={t('broker.incoming.empty')} />}
      <Card>
        <h2 className="mb-2">{t('broker.incoming.ifDecline')}</h2>
        <p className="text-fg-2 text-[14px]">{t('broker.incoming.declineHint')}</p>
        <div className="flex flex-wrap gap-2 mt-2.5">
          {(['outOfArea', 'noSlot', 'propertyType', 'other'] as const).map((k) => <Chip key={k} form="mint">{t(`broker.incoming.reasons.${k}`)}</Chip>)}
        </div>
      </Card>
      <DeclineDialog br={declining} onClose={() => setDeclining(null)} loading={act.isPending} onConfirm={(reason) => declining && act.mutate({ id: declining.id, action: 'decline', reason })} />
    </div>
  );
}
